import React, { useState } from "react";
import { Container, Card, CardBody, Button, Spinner } from "reactstrap";
import { perPageData, showToastMessage } from "../../../../common/service/CommonService";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import CommonOffcanvasLayout from "../../../../Components/Common/CommonOffcanvasLayout";
import tableStyles from "../../../../assets/scss/common/table.module.scss";
import { useTrainingGroupService } from "../api/useTrainingGroupQuery";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import CommonButton from "../../../../Components/Common/CommonButton";
import { TrainingGroupFormValues, TrainingGroupRecord } from "../helper/traininggroup.types";
import { getTrainingGroupTableColumns } from "../helper/traininggroupconstant";
import { queryClient } from "../../../../api/queryClient";
import { useNavigate } from "react-router-dom";
import { MASTER_TG_PATHS } from "../../../../Routes/paths";
import AddEditTrainingGroup from "../components/AddEditTrainingGroup";
import Reason from "../../../../common/utilities/Reason/Reason";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";

const TrainingGroupList: React.FC = () => {
  const navigate = useNavigate();
  document.title = "Training Group | ZENTIXS VISTA";

  //-------------------------------------------------------------------------------------------
  //-->Use STATE
  //-------------------------------------------------------------------------------------------

  const [searchText, setSearchText] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [editRecord, setEditRecord] = useState<TrainingGroupRecord | null>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<TrainingGroupRecord | null>(null);
  const [isReasonModal, setIsReasonModal] = useState<boolean>(false);
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);
  const [updatedRecord, setUpdatedRecord] = useState<TrainingGroupFormValues | null>(null);
  //Audit
  const { SelectAllMutation: AuditMutation } = useAuditService();
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  //-------------------------------------------------------------------------------------------
  //--> Toggles
  //-------------------------------------------------------------------------------------------

  const toggle = (): void => {
    setIsOpen(!isOpen);
    if (isOpen) setEditRecord(null);
  };

  const toggleReasonModal = () => setIsReasonModal(!isReasonModal);
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);
  const toggleDeleteReason = () => setShowDeleteReason(!showDeleteReason);

  // Training Group Service(s)
  const { SelectAllQuery, InsertMutation, UpdateMutation, DeleteMutation } =
    useTrainingGroupService({
      searchText,
      pageNumber: currentPage,
      pageSize: perPageData,
    });

  //-------------------------------------------------------------------------------------------
  //--> Page Start
  //-------------------------------------------------------------------------------------------

  const {
    data: queryResponse,
    isLoading,
    isFetching,
  } = SelectAllQuery as {
    data?: { data?: TrainingGroupRecord[], total_count?: number };
    isLoading: boolean;
    isFetching: boolean;
  };

  const data: TrainingGroupRecord[] = queryResponse?.data ?? [];

  //-------------------------------------------------------------------------------------------
  //--> ON SEARCH
  //-------------------------------------------------------------------------------------------

  const onSearch = (text: string | null) => {
    setSearchText(text);
    setCurrentPage(1);
  };

  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  //-------------------------------------------------------------------------------------------
  //--> On Add
  //-------------------------------------------------------------------------------------------

  const onAdd = (): void => {
    setEditRecord(null);
    toggle();
  };

  const onEdit = (row: TrainingGroupRecord) => {
    setEditRecord(row);
    toggle();
  };

  const onDelete = (row: TrainingGroupRecord) => {
    setSelectedRecord(row);
    toggleDeleteModal();
  };

  const onAssignTraining = (row: TrainingGroupRecord) => {
    if (row.traininggroupid) {
      navigate(MASTER_TG_PATHS.ASSIGN_TRAINING.replace(":id", String(row.traininggroupid)), {
        state: { traininggroup: row.traininggroup }
      });
    }
  };

  //-------------------------------------------------------------------------------------------
  //--> Record Insert
  //-------------------------------------------------------------------------------------------

  const onSave = (values: TrainingGroupFormValues): void => {
    if (editRecord?.traininggroupid) {
      setUpdatedRecord(values);
      toggleReasonModal();
    } else {
      const payload: TrainingGroupRecord = {
        traininggroup: values.traininggroup,
        description: values.description,
      };

      InsertMutation.mutate(payload, {
        onSuccess: (response) => {
          queryClient.invalidateQueries({ queryKey: ["trainingGroup-selectAll"] });
          setSearchText(null);
          setCurrentPage(1);
          showToastMessage("success", response?.message);
          toggle();
        },
        onError: (error) => {
          showToastMessage("error", error?.message ?? error);
        },
      });
    }
  };

  const btnReasonClick = (reasonPayload: { id: number | null; reason: string }) => {
    if (!updatedRecord || !editRecord) return;

    const payload: TrainingGroupRecord = {
      traininggroupid: editRecord.traininggroupid,
      traininggroup: updatedRecord.traininggroup,
      description: updatedRecord.description,
      updatereason: reasonPayload.reason,
    };

    UpdateMutation.mutate(payload, {
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: ["trainingGroup-selectAll"] });
        showToastMessage("success", response?.message);
         setSearchText(null);
         setCurrentPage(1);
        toggleReasonModal();
        toggle();
        setEditRecord(null);
      },
      onError: (error) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  const confirmDelete = (reasonPayload: { id: number | null; reason: string }) => {
    if (!selectedRecord) return;

    const payload = {
      id: selectedRecord.traininggroupid || null,
      updatereason: reasonPayload.reason,
    };

    DeleteMutation.mutate(payload, {
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: ["trainingGroup-selectAll"] });
         showToastMessage("success", response?.message);
         setSearchText(null);
         const totalRecords = data[0]?.total_count || 0;
         const lastPage = Math.ceil((totalRecords - 1) / perPageData);
         setCurrentPage(
         Math.max(1, Math.min(currentPage, lastPage)),
        );
        toggleDeleteModal();
        setShowDeleteReason(false);
        setSelectedRecord(null);
      },
      onError: (error) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Training Group",
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Training_Group_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  //-------------------------------------------------------------------------------------------
  //--> Reset Form
  //------------------------------------------------------------------------------------------

  const btnReset = () => {
    toggle();
  };

  return (
    <div className="page-content">
      <Container fluid className="px-0">
        <Card className={tableStyles.card}>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <CommonSearch onSearch={onSearch} searchText={searchText} />
              <div className="d-flex gap-2">
                <CommonButton
                module={PERMISSION_MODULES.TRAINING_GROUP}
                action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                <CommonButton
                  module={PERMISSION_MODULES.TRAINING_GROUP}
                  action={PERMISSION_ACTIONS.ADD} 
                  color="primary"
                  size="sm"
                  onClick={onAdd}
                  outline
                >
                  <i className="ri-add-line me-1"></i> Add
                </CommonButton>
              </div>
            </div>
            <CommonTableIndexWise
              moduleName={PERMISSION_MODULES.TRAINING_GROUP}
              columns={getTrainingGroupTableColumns(onAssignTraining)}
              data={data}
              showStatus={false}
              actions={{
                onEdit,
                onDelete,
              }}
              totalRecords={
                queryResponse?.data?.[0]?.total_count || 0
              }
              pageSize={perPageData}
              currentPage={currentPage}
              onPageChange={onPageChange}
              loading={isLoading || isFetching}
            />
          </CardBody>
        </Card>

        <CommonOffcanvasLayout
          isOpen={isOpen}
          toggle={btnReset}
          title={editRecord ? `Edit Training Group ( ${editRecord.traininggroup} )` : "Add Training Group"}
        >
          <AddEditTrainingGroup
            existingData={editRecord as any}
            onSave={onSave}
            onCancel={btnReset}
            loading={InsertMutation.isPending}
          />
        </CommonOffcanvasLayout>

        {isReasonModal && editRecord?.traininggroupid != null && (
          <Reason
            isOpen={isReasonModal}
            toggle={toggleReasonModal}
            action="Update"
            id={editRecord.traininggroupid}
            click={btnReasonClick}
            isLoading={UpdateMutation.isPending}
          />
        )}

        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            toggle={toggleDeleteModal}
            recordId={selectedRecord?.traininggroupid ?? 0}
            recordName={selectedRecord?.traininggroup}
            onDeleteClick={confirmDelete}
            isLoading={DeleteMutation.isPending}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={toggleDeleteReason}
          />
        )}

        {isPreviewOpen && (
          <FilePreviewModal
            file={previewFile}
            isOpen={isPreviewOpen}
            size={"xl"}
            onClose={() => {
              setIsPreviewOpen(false);
              setPreviewFile(null);
            }}
          />
        )}

      </Container>
    </div>
  );
};

export default TrainingGroupList;
